/**
 * Attachments section for a requirement — upload to OBS, metadata to BSP.
 * Supports multi-file upload and download links.
 */

import { useEffect, useState } from 'react';
import { Button, Typography, Space, Popconfirm, message, Upload, Spin } from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { listAttachments, createAttachment, deleteAttachment } from '@/api/requirements';
import { getUploadUrl, getDownloadUrl } from '@/api/storage';
import type { RequirementAttachment } from '@/types';
import { useT } from '@/i18n';
import { localeFor } from '@/utils';

const { Text, Link } = Typography;

const formatFileSize = (bytes: number) => {
  if (!bytes || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface RequirementAttachmentsProps {
  projectId: number;
  requirementId: string;
}

const RequirementAttachments: React.FC<RequirementAttachmentsProps> = ({
  projectId,
  requirementId,
}) => {
  const { t, lang } = useT();
  const [attachments, setAttachments] = useState<RequirementAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchAttachments = async () => {
    setLoading(true);
    try {
      const data = await listAttachments(projectId, requirementId);
      setAttachments(data);
    } catch {
      message.error(t('加载附件失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [projectId, requirementId]);

  const uploadOneFile = async (file: File): Promise<RequirementAttachment> => {
    const { url, object_key } = await getUploadUrl(
      file.name,
      file.type || 'application/octet-stream'
    );
    await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    return createAttachment(projectId, requirementId, {
      file_name: file.name,
      file_url: object_key,
      object_key,
      file_size: file.size,
    });
  };

  const handleUpload = async (fileList: File[]) => {
    if (fileList.length === 0) return;
    setUploading(true);
    const results: RequirementAttachment[] = [];
    let failCount = 0;

    for (const file of fileList) {
      try {
        const att = await uploadOneFile(file);
        results.push(att);
      } catch {
        failCount++;
      }
    }

    if (results.length > 0) {
      setAttachments([...results.reverse(), ...attachments]);
      message.success(t('{count} 个附件上传成功', { count: results.length }));
    }
    if (failCount > 0) {
      message.error(t('{count} 个附件上传失败', { count: failCount }));
    }
    setUploading(false);
  };

  const handleDownload = async (att: RequirementAttachment) => {
    try {
      const { url } = await getDownloadUrl(att.file_url);
      window.open(url, '_blank');
    } catch {
      message.error(t('获取下载链接失败'));
    }
  };

  const handleDelete = async (attachmentId: number) => {
    try {
      await deleteAttachment(projectId, requirementId, attachmentId);
      setAttachments(attachments.filter((a) => a.id !== attachmentId));
      message.success(t('附件已删除'));
    } catch {
      message.error(t('删除附件失败'));
    }
  };

  return (
    <div>
      <Upload
        multiple
        beforeUpload={(_file, fileList) => {
          handleUpload(fileList as unknown as File[]);
          return false;
        }}
        showUploadList={false}
      >
        <Button icon={<UploadOutlined />} loading={uploading} size="small" style={{ marginBottom: 12 }}>
          {t('上传附件')}
        </Button>
      </Upload>

      {loading ? (
        <Spin size="small" />
      ) : attachments.length === 0 ? (
        <Text type="secondary">{t('暂无附件')}</Text>
      ) : (
        <div>
          {attachments.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0',
                borderBottom: '1px solid #f5f5f5',
              }}
            >
              <PaperClipOutlined style={{ color: '#999', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Link
                    style={{ fontSize: 13 }}
                    onClick={() => handleDownload(item)}
                    ellipsis
                  >
                    {item.file_name}
                  </Link>
                  {item.file_size > 0 && (
                    <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                      {formatFileSize(item.file_size)}
                    </Text>
                  )}
                </div>
                <Space size={8} style={{ fontSize: 12 }}>
                  <Text type="secondary">
                    {item.created_by?.display_name || item.created_by?.username}
                  </Text>
                  <Text type="secondary">
                    {new Date(item.created_at).toLocaleString(localeFor(lang))}
                  </Text>
                </Space>
              </div>
              <Popconfirm title={t('确认删除？')} onConfirm={() => handleDelete(item.id)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequirementAttachments;
